<template>
    <div class="navbar">
        <div class="grid-container fluid">
            <div class="align-middle grid-x align-justify">
                <div class="cell shrink">
                    <div class="align-middle grid-x">
                        <div class="cell shrink hamburger-button-container" @click="openMenuModal">
                            <span v-html="Icon.MENU"></span>
                        </div>
                        <div class="cell shrink navbar-logo-container">
                            <router-link :to="{name: 'food'}" class="navbar-logo">
                                <img :src="storeLogo" alt="Store Logo"/>
                            </router-link>
                        </div>
                        <div class="align-middle cell shrink flex-container navbar-item" v-if="isStoreOpen">
                            <pickup-time-modal dusk="pickup-time-modal"></pickup-time-modal>
                        </div>
                    </div>
                </div>
                <div class="cell shrink navbar-item">
                    <div class="align-middle grid-x navbar-item">
                        <div class="cell shrink navbar-item">
                            <div class="text-center align-middle grid-x grid-padding-x navbar-item">
                                <div class="cell shrink" :class="['navbar-link', {selected: isMainPage && !isLoginModalOpen && !isMenuModalOpen}]" v-if="isStoreOpen">
                                    <a class="bold" @click.prevent="goTo('food')">Menu</a>
                                    <span class="link-border"></span>
                                </div>
                                <div v-if="currentUser" class="cell shrink" :class="['navbar-link', {selected: $route.name === 'orders' && !isLoginModalOpen && !isMenuModalOpen}]">
                                    <a class="bold" @click.prevent="goTo('orders')">My Orders</a>
                                    <span class="link-border"></span>
                                </div>
                                <div v-if="currentUser" class="cell shrink" :class="['navbar-link', {selected: $route.name === 'account' && !isLoginModalOpen && !isMenuModalOpen}]">
                                    <a class="bold" @click.prevent="goTo('account')">My Account</a>
                                    <span class="link-border"></span>
                                </div>
                                <a v-if="!hasProducts && !currentUser" class="align-middle cell shrink" :class="['navbar-login', {selected: $route.name === 'account' && !isLoginModalOpen && !isMenuModalOpen}]" @click.prevent="openLoginModal">
                                    <span class="login-icon" v-html="Icon.LOGIN"></span>
                                    <span class="bold">Sign In</span>
                                </a>
                            </div>
                        </div>
                        <div class="cell shrink navbar-item navbar-cart-item">
                            <cart-icon></cart-icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component';
import { Icon } from '@/front/icons';
import PickupTimeModal from '@/components/front/time-scheduler/PickupTimeModal.vue';
import { getModule } from 'vuex-module-decorators';
import User from '@/models/user';
import UserModule from '@/stores/modules/user';
import FoodModule from '@/stores/modules/food';
import CartIcon from '@/components/front/cart/CartIcon.vue';
import Settings, { SettingNamespace } from '@/utils/settings';
import CartModule from '@/stores/modules/cart';
import { isStoreOpen } from '@/utils/store-schedule';


@Component({
    components: { PickupTimeModal, CartIcon }
})
export default class Navbar extends Vue
{
    userModule!: UserModule;
    foodModule!: FoodModule;
    cartModule!: CartModule;
    storeLogo: string = "";
    isStoreOpen: boolean = false;

    public async created(): Promise<void> {
        this.userModule = getModule(UserModule, this.$store);
        this.foodModule = getModule(FoodModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);

        const settings = await Settings.getInstance();
        this.storeLogo = settings.get(SettingNamespace.GENERAL, 'store_logo')?.value;
        this.isStoreOpen = await isStoreOpen();
    }

    openMenuModal() {
        this.foodModule.openMenuModal();
    }

    goTo(name: string) {
        if (this.userModule.loginModalOpen) this.userModule.closeLoginModal();
        if (this.foodModule.menuModalOpen) this.foodModule.closeMenuModal();
        this.$router.push({ name: name });
    }

    openLoginModal() {
        this.userModule.openLoginModal();
    }


    get isLoginModalOpen() {
        return this.userModule.loginModalOpen;
    }

    get isMenuModalOpen() {
        return this.foodModule.menuModalOpen;
    }

    get isMainPage() {
        if (this.$route.name !== null && this.$route.name !== undefined) {
            return ['food', 'product-page', 'product-type', 'create-product', 'edit-product'].indexOf(this.$route.name) !== -1;
        }
        return false;
    }

    get hasProducts() {
        return this.cartModule.productsQuantity > 0;
    }

    get Icon() {
        return Icon;
    }

    get currentUser(): User | null {
        return this.userModule.currentUser;
    }
}
</script>
<style lang="scss" scoped>
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.navbar {
    height: 74px;
    width: 100%;
    position: fixed;
    background: var(--navbar-bg);
    top: 0;
    z-index: 5;
    box-shadow: var(--navbar-shadow);

    > .grid-container {
        height: 100%;
        > .grid-x {
            height: 100%;
        }
    }


    .navbar-logo {
        letter-spacing: 1px;
        display: flex;
        align-items: center;

        img {
            width: 100%;
            height: 100%;
            max-height: 55px;
        }
    }

    .navbar-item {
        height: 100%;
    }

    .navbar-login {
        display: flex;
        background: var(--navbar-login-bg);
        padding: var(--navbar-login-padding);
        border-radius: var(--navbar-login-radius);

        .login-icon {
            height: 20px;
            padding-right: 10px;

            path {
                fill: var(--navbar-login-icon-color);
            }
        }

        span {
            color: var(--navbar-login-color);
        }

        &:hover {
            background: rgba(255, 255, 255, 0.10);
        }
    }

    .navbar-link {
        a {
            color: var(--navbar-link-color);
            transition: color ease 0.3s;

            &:hover {
                color: var(--navbar-link-hover-color);
            }
        }

        .link-border {
            display: block;
            height: var(--navbar-link-selected-border-height);
            background: var(--navbar-link-selected-border-color);
            border-radius: var(--navbar-link-selected-border-radius);
            opacity: 0;
            transition: opacity ease 0.3s;
        }

        &.selected {
            a {
                color: var(--navbar-link-selected-color);
            }

            .link-border {
                opacity: 1;
            }
        }
    }
}
</style>
<style lang="scss">
.navbar {
    .login-icon {
        path {
            fill: var(--navbar-login-icon-color);
        }
    }

    .hamburger-button-container {
        cursor:pointer;
        position: relative;
        top: 3px;
        margin-right: 23px;

        path {
            fill: var(--navbar-hamburger-color);
        }
    }
}

</style>
